import { HexColorPicker } from 'react-colorful';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Palette,
    Blend,
    Monitor,
    Moon,
    Pipette,
} from 'lucide-react';

import FontDropdown from '../../ui/FontDropdown';
import GenericDropdown from '../../ui/GenericDropdown';
import OverrideField from './OverrideField';
import { AgentAppOverrides } from './appOverrides';
import AgentTabHeader from './AgentTabHeader';

export interface AppAppearanceTabProps {
    appOverrides: AgentAppOverrides;
    setAppOverrides: (updater: (prev: AgentAppOverrides) => AgentAppOverrides) => void;

    // Website base values for inherit previews
    baseColor: string;
    baseEndColor: string;
    baseColorScheme: string;
    baseFont: string;
    baseWidgetPosition: string;
    baseHideAimdoc: boolean;
    baseDarkModeEnabled: boolean;
    baseDarkModeDefault: 'system' | 'light' | 'dark';
    baseDarkModeColor: string;
}

const ColorSwatch = ({ value }: { value: string }) => (
    <span
        className="inline-block w-5 h-5 rounded border"
        style={{ backgroundColor: value }}
        aria-label={value}
    />
);

export default function AppAppearanceTab({
    appOverrides,
    setAppOverrides,
    baseColor,
    baseEndColor,
    baseColorScheme,
    baseFont,
    baseWidgetPosition,
    baseHideAimdoc,
    baseDarkModeEnabled,
    baseDarkModeDefault,
    baseDarkModeColor,
}: AppAppearanceTabProps) {
    // -- Colors
    const overrideColor = appOverrides.color ?? null;
    const overrideEndColor = appOverrides.endColor ?? null;
    const overrideScheme = appOverrides.scheme ?? null;
    const isColorOverridden =
        overrideColor !== null
        || overrideEndColor !== null
        || overrideScheme !== null;

    // -- Font
    const overrideFont = appOverrides.font ?? null;
    const isFontOverridden = overrideFont !== null && overrideFont !== undefined;

    // -- Layout
    const overrideWidgetPosition = appOverrides.widgetPosition ?? null;
    const isLayoutOverridden =
        overrideWidgetPosition !== null && overrideWidgetPosition !== undefined;

    // -- Expanded layout (in-app the collapsed state is always the command-bar
    // pill, so only the expanded mode is configurable; copilot's centered
    // modal isn't offered because it covers the page the agent acts on).
    // App mode does NOT inherit the website's expanded mode — it defaults to
    // the sidebar (see resolveConfigForMode in the widget).
    const overrideExpandedMode = appOverrides.expandedMode ?? null;
    const isExpandedModeOverridden =
        overrideExpandedMode !== null && overrideExpandedMode !== undefined;

    // -- Branding
    const overrideHideAimdoc = appOverrides.hideAimdoc ?? null;
    const isHideAimdocOverridden =
        overrideHideAimdoc !== null && overrideHideAimdoc !== undefined;

    // -- Dark mode
    const overrideDarkEnabled = appOverrides.darkModeEnabled ?? null;
    const overrideDarkDefault = appOverrides.darkModeDefault ?? null;
    const overrideDarkColor = appOverrides.darkModeColor ?? null;
    const isDarkModeOverridden =
        overrideDarkEnabled !== null
        || overrideDarkDefault !== null
        || overrideDarkColor !== null;

    const removeKeys = (keys: (keyof AgentAppOverrides)[]) =>
        setAppOverrides((prev) => {
            const next = { ...prev };
            for (const key of keys) {
                delete (next as any)[key];
            }
            return next;
        });

    return (
        <div className="w-full">
            <AgentTabHeader
                icon={Palette}
                title="App mode appearance"
                description="Inherit your website design or customize the widget inside your product."
            />

            <div className="mx-auto w-full max-w-4xl space-y-4 py-6">
            {/* Colors */}
            <OverrideField
                label="Colors"
                description="Primary color, scheme, and optional gradient end color."
                isOverridden={isColorOverridden}
                onCustomize={() =>
                    setAppOverrides((prev) => ({
                        ...prev,
                        color: baseColor,
                        endColor: baseEndColor,
                        scheme: baseColorScheme,
                    }))
                }
                onReset={() => removeKeys(['color', 'endColor', 'scheme', 'mixedColor', 'darkerColor', 'lighterColor'])}
                renderBasePreview={() => (
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <ColorSwatch value={baseColor} /> {baseColor}
                        </div>
                        {baseColorScheme === 'Gradient' && (
                            <div className="flex items-center gap-2">
                                <ColorSwatch value={baseEndColor} /> {baseEndColor}
                            </div>
                        )}
                        <span className="text-muted-foreground">({baseColorScheme})</span>
                    </div>
                )}
            >
                <div className="space-y-4">
                    <div>
                        <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                            <Blend className="h-4 w-4 text-muted-foreground" /> Scheme
                        </Label>
                        <GenericDropdown
                            value={overrideScheme ?? baseColorScheme}
                            selectValues={['Solid', 'Gradient']}
                            setValue={(v: string) =>
                                setAppOverrides((prev) => ({ ...prev, scheme: v }))
                            }
                            defaultValue="Solid"
                        />
                    </div>
                    <div className="grid grid-cols-1 @xl:grid-cols-2 gap-6">
                        <div>
                            <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                <Pipette className="h-4 w-4 text-muted-foreground" /> Primary color
                            </Label>
                            <HexColorPicker
                                color={overrideColor ?? baseColor}
                                onChange={(v) =>
                                    setAppOverrides((prev) => ({ ...prev, color: v }))
                                }
                            />
                            <Input
                                className="mt-2"
                                value={overrideColor ?? baseColor}
                                onChange={(e) =>
                                    setAppOverrides((prev) => ({
                                        ...prev,
                                        color: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        {(overrideScheme ?? baseColorScheme) === 'Gradient' && (
                            <div>
                                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                    <Palette className="h-4 w-4 text-muted-foreground" /> Gradient end color
                                </Label>
                                <HexColorPicker
                                    color={overrideEndColor ?? baseEndColor}
                                    onChange={(v) =>
                                        setAppOverrides((prev) => ({ ...prev, endColor: v }))
                                    }
                                />
                                <Input
                                    className="mt-2"
                                    value={overrideEndColor ?? baseEndColor}
                                    onChange={(e) =>
                                        setAppOverrides((prev) => ({
                                            ...prev,
                                            endColor: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </OverrideField>

            {/* Font */}
            <OverrideField
                label="Font"
                description="Override the widget font when running in app mode."
                isOverridden={isFontOverridden}
                onCustomize={() =>
                    setAppOverrides((prev) => ({ ...prev, font: baseFont }))
                }
                onReset={() => removeKeys(['font'])}
                renderBasePreview={() => <span>{baseFont}</span>}
            >
                <FontDropdown
                    value={overrideFont ?? baseFont}
                    setValue={(v: string) =>
                        setAppOverrides((prev) => ({ ...prev, font: v }))
                    }
                />
            </OverrideField>

            {/* Widget position. Center is app-only: the prompt bar's starting
                spot (users can drag it anywhere); a sidebar still opens right. */}
            <OverrideField
                label="Widget position"
                description="Where the prompt bar starts in app mode. Users can drag it anywhere; the sidebar opens on the right unless this is Left."
                isOverridden={isLayoutOverridden}
                onCustomize={() =>
                    setAppOverrides((prev) => ({
                        ...prev,
                        widgetPosition: baseWidgetPosition,
                    }))
                }
                onReset={() => removeKeys(['widgetPosition'])}
                renderBasePreview={() => <span>{baseWidgetPosition}</span>}
            >
                <Select
                    value={overrideWidgetPosition ?? baseWidgetPosition}
                    onValueChange={(v) =>
                        setAppOverrides((prev) => ({ ...prev, widgetPosition: v }))
                    }
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                </Select>
            </OverrideField>

            {/* Expanded layout */}
            <OverrideField
                label="Expanded layout"
                description="How the widget opens in app mode: a full-height sidebar that pushes your app's content aside (default), or a corner chat window."
                isOverridden={isExpandedModeOverridden}
                onCustomize={() =>
                    setAppOverrides((prev) => ({
                        ...prev,
                        expandedMode: 'sidebar',
                    }))
                }
                onReset={() => removeKeys(['expandedMode'])}
                renderBasePreview={() => <span>Sidebar (app mode default)</span>}
            >
                <Select
                    value={overrideExpandedMode === 'sidebar' ? 'sidebar' : 'chat'}
                    onValueChange={(v) =>
                        setAppOverrides((prev) => ({
                            ...prev,
                            expandedMode: v as 'chat' | 'sidebar',
                        }))
                    }
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="chat">Chat window</SelectItem>
                        <SelectItem value="sidebar">Sidebar</SelectItem>
                    </SelectContent>
                </Select>
            </OverrideField>

            {/* Hide Aimdoc */}
            <OverrideField
                label="Aimdoc branding"
                description="Hide or show the 'Powered by Aimdoc' badge in app mode."
                isOverridden={isHideAimdocOverridden}
                onCustomize={() =>
                    setAppOverrides((prev) => ({ ...prev, hideAimdoc: baseHideAimdoc }))
                }
                onReset={() => removeKeys(['hideAimdoc'])}
                renderBasePreview={() => (
                    <span>
                        Hidden: {baseHideAimdoc ? 'yes' : 'no'}
                    </span>
                )}
            >
                <div className="flex items-center justify-between">
                    <Label htmlFor="in-app-hide-aimdoc" className="text-sm">
                        Hide "Powered by Aimdoc"
                    </Label>
                    <Switch
                        id="in-app-hide-aimdoc"
                        checked={overrideHideAimdoc ?? false}
                        onCheckedChange={(v) =>
                            setAppOverrides((prev) => ({ ...prev, hideAimdoc: v }))
                        }
                    />
                </div>
            </OverrideField>

            {/* Dark mode */}
            <OverrideField
                label="Dark mode"
                description="Override dark-mode behavior for the app mode experience."
                isOverridden={isDarkModeOverridden}
                onCustomize={() =>
                    setAppOverrides((prev) => ({
                        ...prev,
                        darkModeEnabled: baseDarkModeEnabled,
                        darkModeDefault: baseDarkModeDefault,
                        darkModeColor: baseDarkModeColor,
                    }))
                }
                onReset={() =>
                    removeKeys(['darkModeEnabled', 'darkModeDefault', 'darkModeColor'])
                }
                renderBasePreview={() => (
                    <div className="space-y-1">
                        <div>Enabled: {baseDarkModeEnabled ? 'yes' : 'no'}</div>
                        <div>Default: {baseDarkModeDefault}</div>
                        <div className="flex items-center gap-2">
                            Color: <ColorSwatch value={baseDarkModeColor} /> {baseDarkModeColor}
                        </div>
                    </div>
                )}
            >
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="in-app-dark-enabled" className="text-sm flex items-center gap-2">
                            <Moon className="h-4 w-4 text-muted-foreground" /> Dark mode enabled
                        </Label>
                        <Switch
                            id="in-app-dark-enabled"
                            checked={overrideDarkEnabled ?? false}
                            onCheckedChange={(v) =>
                                setAppOverrides((prev) => ({ ...prev, darkModeEnabled: v }))
                            }
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-sm flex items-center gap-2">
                            <Monitor className="h-4 w-4 text-muted-foreground" /> Default theme
                        </Label>
                        <Select
                            value={overrideDarkDefault ?? baseDarkModeDefault}
                            onValueChange={(v) =>
                                setAppOverrides((prev) => ({
                                    ...prev,
                                    darkModeDefault: v as 'system' | 'light' | 'dark',
                                }))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="system">System</SelectItem>
                                <SelectItem value="light">Light</SelectItem>
                                <SelectItem value="dark">Dark</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-sm mb-2 block">Dark mode accent color</Label>
                        <HexColorPicker
                            color={overrideDarkColor ?? baseDarkModeColor}
                            onChange={(v) =>
                                setAppOverrides((prev) => ({ ...prev, darkModeColor: v }))
                            }
                        />
                        <Input
                            className="mt-2"
                            value={overrideDarkColor ?? baseDarkModeColor}
                            onChange={(e) =>
                                setAppOverrides((prev) => ({
                                    ...prev,
                                    darkModeColor: e.target.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </OverrideField>
            </div>
        </div>
    );
}
